<script setup>
import { ref, computed, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { useToast } from "@/composables/useToast.js";

const props = defineProps({
    gateway: {
        type: Object,
        required: true
    }
});

const { success, error, warning } = useToast();

const loading = ref(false);
const showMasterSeedModal = ref(false);
const masterSeed = ref('');
const confirmMasterSeed = ref('');
const seedStatus = ref({
    has_master_seed: false,
    is_encrypted: false
});
const generatedSeed = ref('');
const fundingWalletData = ref(null);
const showSeed = ref(false);
const isTronGateway = computed(() => {
    return props.gateway && (
        props.gateway.slug === 'tron' || 
        props.gateway.slug === 'tron-network' || 
        props.gateway.slug === 'tron-usdt'
    );
});

onMounted(async () => {
    if (isTronGateway.value) {
        await loadSeedStatus();
    }
});

const loadSeedStatus = async () => {
    try {
        loading.value = true;
        const response = await fetch(`/admin/payment-gateways/${props.gateway.id}/tron/master-seed-status`);
        const data = await response.json();
        
        if (data.success) {
            seedStatus.value = {
                has_master_seed: data.has_master_seed,
                is_encrypted: data.is_encrypted
            };
        }
    } catch (err) {
        console.error('Failed to load seed status:', err);
    } finally {
        loading.value = false;
    }
};

const generateNewSeed = async () => {
    try {
        loading.value = true;
        const response = await fetch('/admin/payment-gateways/tron/generate-master-seed', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            }
        });
        
        const data = await response.json();
        
        if (data.success) {
            generatedSeed.value = data.master_seed;
            masterSeed.value = data.master_seed;
            confirmMasterSeed.value = data.master_seed; // Auto-fill confirmation
            fundingWalletData.value = data.funding_wallet; // Store funding wallet info
            showSeed.value = true; // Show the seed by default
            success('✓ Secure master seed generated! Download backup before saving.');
        } else {
            error(data.message || 'Failed to generate master seed');
        }
    } catch (err) {
        error('Failed to generate master seed. Please try again.');
        console.error(err);
    } finally {
        loading.value = false;
    }
};

const saveMasterSeed = async () => {
    // Validation
    if (!masterSeed.value) {
        error('Please enter or generate a master seed');
        return;
    }
    
    if (masterSeed.value.length !== 64) {
        error('Master seed must be exactly 64 characters');
        return;
    }
    
    if (!/^[a-fA-F0-9]{64}$/.test(masterSeed.value)) {
        error('Master seed must contain only hexadecimal characters (0-9, a-f)');
        return;
    }
    
    if (masterSeed.value !== confirmMasterSeed.value) {
        error('Master seeds do not match');
        return;
    }

    try {
        loading.value = true;
        const response = await fetch(`/admin/payment-gateways/${props.gateway.id}/tron/update-master-seed`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            },
            body: JSON.stringify({
                master_seed: masterSeed.value
            })
        });
        
        const data = await response.json();
        
        if (data.success) {
            // Store funding wallet data for backup if provided
            if (data.funding_wallet) {
                fundingWalletData.value = data.funding_wallet;
            }
            
            success('✅ Master seed saved and encrypted successfully!');
            
            // Show notification about regenerating wallets if this is an update
            if (seedStatus.value.has_master_seed) {
                warning('⚠️ IMPORTANT: Go to TRON Wallet Management to regenerate all user wallets with the new master seed!');
            }
            
            showMasterSeedModal.value = false;
            masterSeed.value = '';
            confirmMasterSeed.value = '';
            generatedSeed.value = '';
            await loadSeedStatus();
        } else {
            error(data.message || 'Failed to save master seed');
        }
    } catch (err) {
        error('Failed to save master seed. Please try again.');
        console.error(err);
    } finally {
        loading.value = false;
    }
};

const downloadSeedBackup = () => {
    if (!generatedSeed.value) return;
    
    const backupContent = [
        `TRON MASTER SEED - BACKUP\n`,
        `================================\n\n`,
        `⚠️ CRITICAL: Keep this safe!\n`,
        `Loss of this seed = Loss of all user funds!\n\n`,
        `Gateway: ${props.gateway.name}\n`,
        `Date: ${new Date().toISOString()}\n\n`,
        `Master Seed:\n`,
        `${generatedSeed.value}\n\n`
    ];
    
    // Add funding wallet info if available
    if (fundingWalletData.value) {
        backupContent.push(
            `================================\n`,
            `FUNDING WALLET (Derivation Index 20)\n`,
            `================================\n\n`,
            `⚠️ This wallet is used to auto-fund user wallets with TRX for gas fees\n\n`,
            `Funding Wallet Address:\n`,
            `${fundingWalletData.value.address}\n\n`,
            `Funding Wallet Private Key:\n`,
            `${fundingWalletData.value.private_key}\n\n`
        );
    }
    
    backupContent.push(
        `================================\n`,
        `Store this file in a secure location:\n`,
        `- Encrypted USB drive\n`,
        `- Password manager\n`,
        `- Secure cloud storage (encrypted)\n`,
        `- Physical paper wallet (offline)\n`
    );
    
    const blob = new Blob(backupContent, { type: 'text/plain' });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tron-master-seed-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    success('Backup file downloaded. Keep it safe!');
};

const copySeedToClipboard = () => {
    if (!generatedSeed.value) return;
    
    navigator.clipboard.writeText(generatedSeed.value).then(() => {
        success('Master seed copied to clipboard');
    }).catch(err => {
        error('Failed to copy to clipboard');
        console.error(err);
    });
};

const openModal = () => {
    showMasterSeedModal.value = true;
    masterSeed.value = '';
    confirmMasterSeed.value = '';
    generatedSeed.value = '';
    showSeed.value = false;
};

const closeModal = () => {
    showMasterSeedModal.value = false;
    masterSeed.value = '';
    confirmMasterSeed.value = '';
    generatedSeed.value = '';
    showSeed.value = false;
};
</script>

<template>
  <div
    v-if="isTronGateway"
    class="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl shadow-sm border-2 border-purple-200 dark:border-purple-700 p-6"
  >
    <div class="flex items-start justify-between mb-4">
      <div class="flex items-start space-x-3">
        <div class="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
          <svg
            class="w-6 h-6 text-purple-600 dark:text-purple-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <div>
          <h3 class="text-lg font-bold text-gray-900 dark:text-white">
            TRON Master Seed Configuration
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Secure encrypted storage for HD wallet derivation
          </p>
        </div>
      </div>
    </div>

    <div class="space-y-4">
      <!-- Status Display -->
      <div class="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div v-if="seedStatus.has_master_seed" class="flex items-center space-x-2">
              <svg
                class="w-5 h-5 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="text-sm font-medium text-gray-900 dark:text-gray-100">Master Seed Configured</span>
            </div>
            <div v-else class="flex items-center space-x-2">
              <svg
                class="w-5 h-5 text-amber-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="text-sm font-medium text-amber-700 dark:text-amber-400">No Master Seed Set</span>
            </div>
          </div>
          
          <div class="flex items-center space-x-2">
            <span
              v-if="seedStatus.is_encrypted"
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
            >
              <svg
                class="w-3 h-3 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clip-rule="evenodd"
                />
              </svg>
              Encrypted
            </span>
            <span
              v-else-if="seedStatus.has_master_seed"
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
            >
              <svg
                class="w-3 h-3 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              Plain Text
            </span>
          </div>
        </div>
        
        <!-- Warning if not encrypted -->
        <div
          v-if="seedStatus.has_master_seed && !seedStatus.is_encrypted"
          class="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg"
        >
          <p class="text-xs text-amber-800 dark:text-amber-300">
            ⚠️ Your master seed is stored in plain text. Click "Update Master Seed" to encrypt it.
          </p>
        </div>
      </div>

      <!-- Action Button -->
      <button
        type="button"
        class="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
        @click="openModal"
      >
        <svg
          class="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            v-if="!seedStatus.has_master_seed"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
          <path
            v-else
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
        {{ seedStatus.has_master_seed ? 'Update Master Seed' : 'Configure Master Seed' }}
      </button>

      <!-- Security Notice -->
      <div class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
        <div class="flex items-start space-x-2">
          <svg
            class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clip-rule="evenodd"
            />
          </svg>
          <div class="flex-1">
            <p class="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
              Security Information
            </p>
            <ul class="text-xs text-blue-800 dark:text-blue-400 space-y-1">
              <li>• Master seed is encrypted using AES-256-CBC encryption</li>
              <li>• Controls all user deposit addresses</li>
              <li>• Required for sweeping funds to main wallet</li>
              <li>• Always backup securely before updating</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <teleport to="body">
      <div
        v-if="showMasterSeedModal"
        class="fixed inset-0 z-[9999] overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-gray-900 bg-opacity-50 dark:bg-black dark:bg-opacity-70 transition-opacity"
          @click="closeModal"
        />

        <!-- Modal Container -->
        <div class="flex min-h-screen items-center justify-center p-4">
          <!-- Modal panel -->
          <div class="relative w-full max-w-3xl transform overflow-visible rounded-2xl bg-white dark:bg-slate-800 shadow-2xl transition-all">
            <div class="bg-white dark:bg-slate-800 px-6 pt-6 pb-4">
              <div class="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-slate-600 pb-4">
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3
                    id="modal-title"
                    class="text-xl font-bold leading-6 text-gray-900 dark:text-white"
                  >
                    {{ seedStatus.has_master_seed ? 'Update TRON Master Seed' : 'Configure TRON Master Seed' }}
                  </h3>
                </div>
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  @click="closeModal"
                >
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <!-- Scrollable content area -->
              <div class="max-h-[calc(100vh-300px)] overflow-y-auto pr-2 space-y-5">
                <!-- Step-by-step Instructions -->
                <div class="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-700 rounded-lg">
                  <div class="flex items-start space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <p class="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-2">Quick Setup Guide</p>
                      <ol class="text-xs text-purple-800 dark:text-purple-400 space-y-1 list-decimal list-inside">
                        <li>Click "Generate New Master Seed" button below</li>
                        <li>Immediately download the backup file</li>
                        <li>Copy seed to your password manager (recommended)</li>
                        <li>Click "Save Encrypted" to store securely</li>
                        <li>Store backup in 3+ secure locations</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <!-- Warning -->
                <div v-if="seedStatus.has_master_seed" class="p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 dark:border-amber-600 mb-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg
                        class="h-5 w-5 text-amber-400 dark:text-amber-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <p class="text-sm text-amber-700 dark:text-amber-400 font-semibold">
                        ⚠️ WARNING: Changing Master Seed Will Break Existing Addresses
                      </p>
                      <p class="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Updating the master seed will invalidate ALL existing user and deposit TRON addresses. 
                        Users will no longer be able to deposit to their old addresses.
                      </p>
                      <div class="mt-3 p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                        <p class="text-xs text-blue-800 dark:text-blue-300 font-semibold mb-2">
                          📋 Required Action After Saving:
                        </p>
                        <ul class="text-xs text-blue-700 dark:text-blue-300 space-y-1 ml-4 list-disc">
                          <li>Go to <strong>TRON Wallet Management</strong> page</li>
                          <li>Click <strong>"Regenerate Wallets"</strong> button</li>
                          <li>Confirm regeneration to update all user addresses</li>
                          <li>Notify users about their new wallet addresses</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg
                        class="h-5 w-5 text-red-400 dark:text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <p class="text-sm text-red-700 dark:text-red-400 font-semibold">
                        CRITICAL: Loss of master seed = Loss of all user funds!
                      </p>
                      <p class="text-xs text-red-600 dark:text-red-400 mt-1">
                        Always backup before updating. This seed controls all TRON deposit addresses.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Generate Button -->
                <div>
                  <button
                    type="button"
                    class="w-full inline-flex items-center justify-center px-6 py-4 border-2 border-dashed border-purple-300 dark:border-purple-600 rounded-xl text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    :disabled="loading"
                    @click="generateNewSeed"
                  >
                    <svg
                      v-if="!loading"
                      class="w-6 h-6 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                      />
                    </svg>
                    <svg
                      v-else
                      class="animate-spin w-6 h-6 mr-3"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {{ loading ? 'Generating Secure Seed...' : '🔐 Generate New Master Seed' }}
                  </button>
                  <p class="mt-2 text-xs text-center text-gray-500 dark:text-gray-400">
                    Creates a cryptographically secure 256-bit seed
                  </p>
                </div>

                <div class="relative">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-300 dark:border-gray-600" />
                  </div>
                  <div class="relative flex justify-center text-sm">
                    <span class="px-2 bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400">or import existing</span>
                  </div>
                </div>

                <!-- Master Seed Input -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Master Seed (64 hex characters)
                    </label>
                    <span
                      v-if="generatedSeed && masterSeed === generatedSeed"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    >
                      <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                      Generated
                    </span>
                  </div>
                  <div class="relative">
                    <input
                      v-model="masterSeed"
                      :type="showSeed ? 'text' : 'password'"
                      class="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                      placeholder="Enter or generate a 64-character hex master seed"
                      maxlength="64"
                    >
                    <button
                      type="button"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      @click="showSeed = !showSeed"
                    >
                      <svg
                        v-if="!showSeed"
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    </button>
                  </div>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {{ masterSeed.length }}/64 characters
                  </p>
                </div>

                <!-- Confirm Master Seed Input -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm Master Seed
                  </label>
                  <input
                    v-model="confirmMasterSeed"
                    :type="showSeed ? 'text' : 'password'"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                    placeholder="Re-enter master seed to confirm"
                    maxlength="64"
                  >
                </div>

                <!-- Backup Options (only show if seed was generated) -->
                <div
                  v-if="generatedSeed"
                  class="p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-600 rounded-xl shadow-sm"
                >
                  <div class="flex items-start space-x-3 mb-3">
                    <div class="flex-shrink-0">
                      <svg class="w-6 h-6 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p class="text-base font-bold text-amber-900 dark:text-amber-300">
                        ⚠️ Backup Required Before Saving!
                      </p>
                      <p class="text-xs text-amber-800 dark:text-amber-400 mt-1">
                        Save this seed in multiple secure locations. Loss of this seed = Loss of all funds!
                      </p>
                    </div>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      class="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                      @click="downloadSeedBackup"
                    >
                      <svg
                        class="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                      📥 Download Backup
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 border-2 border-blue-300 dark:border-blue-700 rounded-lg transition-colors duration-200"
                      @click="copySeedToClipboard"
                    >
                      <svg
                        class="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      📋 Copy to Clipboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- End scrollable content -->

            <!-- Modal footer -->
            <div class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-800 border-t border-gray-200 dark:border-slate-600 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                class="w-full sm:w-auto inline-flex justify-center items-center rounded-xl border-2 border-gray-300 dark:border-gray-600 shadow-sm px-6 py-3 bg-white dark:bg-slate-800 text-base font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                @click="closeModal"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancel
              </button>
              <button
                type="button"
                class="w-full sm:w-auto inline-flex justify-center items-center rounded-xl border border-transparent shadow-lg px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-base font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 disabled:transform-none"
                :disabled="loading || !masterSeed || !confirmMasterSeed"
                @click="saveMasterSeed"
              >
                <svg
                  v-if="loading"
                  class="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {{ loading ? 'Saving & Encrypting...' : '🔐 Save Encrypted' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
